import { env } from './env';
import type { CareerApplicationInput, ContactSubmissionInput } from './forms';
import type {
  KnowledgeBaseResource,
  KnowledgeBaseResourceInput,
  KnowledgeBaseResourceQuery,
} from './knowledge-base';

export interface ServerUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'consultant';
  isAdmin: boolean;
  title?: string | null;
  phone?: string | null;
  bio?: string | null;
  linkedinUrl?: string | null;
  createdAt: string;
}

export interface PublicConsultantProfile {
  id: string;
  email: string;
  name: string;
  title?: string | null;
  bio?: string | null;
  linkedinUrl?: string | null;
  createdAt: string;
}

export interface ServerStateRow {
  storageKey: string;
  payloadText: string;
}

export interface ReferenceListRequestInput {
  fullName: string;
  company?: string;
  jobTitle?: string;
  country?: string;
  email: string;
  phone?: string;
  honeypot?: string;
}

function buildApiUrl(path: string) {
  const base = env.apiBaseUrl.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let data: unknown = {};

  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      throw new Error(response.ok ? 'Server returned an invalid response.' : text);
    }
  }

  if (
    data &&
    typeof data === 'object' &&
    'success' in data &&
    (data as { success?: boolean }).success === false
  ) {
    throw new Error(
      'error' in data && typeof (data as { error?: unknown }).error === 'string'
        ? ((data as { error: string }).error)
        : `Request failed with status ${response.status}.`,
    );
  }

  if (!response.ok) {
    if (data && typeof data === 'object' && 'error' in data && typeof (data as { error?: unknown }).error === 'string') {
      throw new Error((data as { error: string }).error);
    }
    throw new Error(`Request failed with status ${response.status}.`);
  }

  return data as T;
}

async function apiRequest<T>(path: string, init: globalThis.RequestInit = {}) {
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData) && init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('X-Requested-With', 'XMLHttpRequest');

  const response = await fetch(buildApiUrl(path), {
    ...init,
    credentials: 'include',
    headers,
  });

  return parseJsonResponse<T>(response);
}

export function fetchSessionRequest() {
  return apiRequest<{ success: true; user: ServerUser | null }>('/auth.php?action=session');
}

export function loginRequest(email: string, password: string) {
  return apiRequest<{ success: true; user: ServerUser }>('/auth.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'login',
      email,
      password,
    }),
  });
}

export function signupRequest(payload: { name: string; email: string; password: string }) {
  return apiRequest<{ success: true; user: ServerUser }>('/auth.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'signup',
      ...payload,
    }),
  });
}

export function logoutRequest() {
  return apiRequest<{ success: true }>('/auth.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'logout',
    }),
  });
}

export function listUsersRequest() {
  return apiRequest<{ success: true; users: ServerUser[] }>('/users.php');
}

export function fetchPublicConsultantProfilesRequest() {
  return apiRequest<{ success: true; profiles: PublicConsultantProfile[] }>('/users.php?scope=public');
}

export function createUserRequest(user: {
  name: string;
  email: string;
  password: string;
  role: 'admin' | 'consultant';
  isAdmin: boolean;
  title?: string;
  phone?: string;
  bio?: string;
  linkedinUrl?: string;
}) {
  return apiRequest<{ success: true; user: ServerUser }>('/users.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'add',
      user,
    }),
  });
}

export function updateUserRequest(id: string, updates: {
  name?: string;
  email?: string;
  password?: string;
  role?: 'admin' | 'consultant';
  isAdmin?: boolean;
  title?: string;
  phone?: string;
  bio?: string;
  linkedinUrl?: string;
}) {
  return apiRequest<{ success: true; user: ServerUser }>('/users.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'update',
      id,
      updates,
    }),
  });
}

export function updateOwnProfileRequest(updates: {
  title?: string;
  phone?: string;
  bio?: string;
  linkedinUrl?: string;
}) {
  return apiRequest<{ success: true; user: ServerUser }>('/users.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'updateSelf',
      updates,
    }),
  });
}

export function deleteUserRequest(id: string) {
  return apiRequest<{ success: true }>('/users.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'delete',
      id,
    }),
  });
}

export function toggleAdminRequest(id: string) {
  return apiRequest<{ success: true; user: ServerUser }>('/users.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'toggleAdmin',
      id,
    }),
  });
}

export function fetchStateRowsRequest(keys: string[]) {
  return apiRequest<{ success: true; rows: ServerStateRow[] }>('/state.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'read',
      keys,
    }),
  });
}

export function upsertStateRowsRequest(rows: ServerStateRow[]) {
  return apiRequest<{ success: true }>('/state.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'upsert',
      rows,
    }),
  });
}

export function deleteStateKeysRequest(keys: string[]) {
  return apiRequest<{ success: true }>('/state.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'delete',
      keys,
    }),
  });
}

export function submitContactSubmissionRequest(input: ContactSubmissionInput) {
  const formData = new FormData();
  formData.set('action', 'contact');
  formData.set('fullName', input.fullName);
  formData.set('email', input.email);
  formData.set('company', input.company ?? '');
  formData.set('service', input.service ?? '');
  formData.set('message', input.message);
  formData.set('honeypot', input.honeypot ?? '');

  return apiRequest<{ success: true }>('/forms.php', {
    method: 'POST',
    body: formData,
  });
}

export function submitReferenceListRequest(input: ReferenceListRequestInput) {
  const formData = new FormData();
  formData.set('action', 'referenceListRequest');
  formData.set('fullName', input.fullName);
  formData.set('company', input.company ?? '');
  formData.set('jobTitle', input.jobTitle ?? '');
  formData.set('country', input.country ?? '');
  formData.set('email', input.email);
  formData.set('phone', input.phone ?? '');
  formData.set('honeypot', input.honeypot ?? '');

  return apiRequest<{ success: true; contactId: string }>('/forms.php', {
    method: 'POST',
    body: formData,
  });
}

function buildKnowledgeBasePath(action: string, query: Record<string, string | undefined> = {}) {
  const params = new URLSearchParams();
  params.set('action', action);

  Object.entries(query).forEach(([key, value]) => {
    if (typeof value === 'string' && value.trim() !== '') {
      params.set(key, value);
    }
  });

  return `/knowledge-base.php?${params.toString()}`;
}

export function buildKnowledgeBaseDownloadUrl(resourceId: string) {
  return buildApiUrl(buildKnowledgeBasePath('download', { id: resourceId }));
}

export function fetchKnowledgeBaseResourcesRequest(query: KnowledgeBaseResourceQuery = {}) {
  return apiRequest<{ success: true; resources: KnowledgeBaseResource[] }>(
    buildKnowledgeBasePath('list', {
      scope: query.scope,
      status: query.status,
      resourceType: query.resourceType,
      topic: query.topic,
      contentCategory: query.contentCategory,
      serviceLine: query.serviceLine,
      tag: query.tag,
      sector: query.sector,
      q: query.q,
      featured: typeof query.featured === 'boolean' ? (query.featured ? '1' : '0') : undefined,
    }),
    {
      method: 'GET',
    },
  );
}

export function fetchKnowledgeBaseResourceRequest(id: string, scope?: KnowledgeBaseResourceQuery['scope']) {
  return apiRequest<{ success: true; resource: KnowledgeBaseResource }>(
    buildKnowledgeBasePath('get', {
      id,
      scope,
    }),
    {
      method: 'GET',
    },
  );
}

export function saveKnowledgeBaseResourceRequest(payload: KnowledgeBaseResourceInput) {
  const formData = new FormData();
  formData.set('action', 'save');
  if (payload.id) {
    formData.set('id', payload.id);
  }
  formData.set('title', payload.title);
  formData.set('resourceType', payload.resourceType);
  formData.set('sourceType', payload.sourceType);
  formData.set('summary', payload.summary);
  formData.set('topic', payload.topic);
  formData.set('contentCategory', payload.contentCategory);
  formData.set('serviceLine', payload.serviceLine);
  formData.set('language', payload.language);
  formData.set('featured', payload.featured ? '1' : '0');
  formData.set('status', payload.status);
  formData.set('tags', JSON.stringify(payload.tags ?? []));
  formData.set('sectors', JSON.stringify(payload.sectors ?? []));
  if (payload.externalUrl) {
    formData.set('externalUrl', payload.externalUrl);
  }
  if (payload.removeFile) {
    formData.set('removeFile', '1');
  }
  if (payload.file) {
    formData.set('file', payload.file);
  }

  return apiRequest<{ success: true; resource: KnowledgeBaseResource }>('/knowledge-base.php', {
    method: 'POST',
    body: formData,
  });
}

export function deleteKnowledgeBaseResourceRequest(id: string) {
  return apiRequest<{ success: true }>('/knowledge-base.php', {
    method: 'POST',
    body: JSON.stringify({
      action: 'delete',
      id,
    }),
  });
}

export function submitCareerApplicationRequest(input: CareerApplicationInput) {
  const formData = new FormData();
  formData.set('action', 'career');
  formData.set('position', input.position ?? '');
  formData.set('firstName', input.firstName);
  formData.set('lastName', input.lastName);
  formData.set('email', input.email);
  formData.set('phone', input.phone ?? '');
  formData.set('linkedinUrl', input.linkedinUrl ?? '');
  formData.set('coverLetter', input.coverLetter ?? '');
  formData.set('honeypot', input.honeypot ?? '');
  formData.set('cvFile', input.cvFile);

  return apiRequest<{ success: true }>('/forms.php', {
    method: 'POST',
    body: formData,
  });
}

export function uploadSitePhotoRequest(file: File) {
  const formData = new FormData();
  formData.set('action', 'upload');
  formData.set('photoFile', file);

  return apiRequest<{ success: true; url: string; storedName: string }>('/photos.php', {
    method: 'POST',
    body: formData,
  });
}
